import { reactive } from 'vue'

import { apiFetch } from '@ja/portal-query'

import type { CommunicationItem } from '../types/communication'
import { normalizeCommunicationItem } from '../types/communication'

interface ActiveChat {
  conversationId: string
  communicationId?: string
  title: string
  recipientName?: string
  minimized: boolean
}

export const messengerState = reactive({
  activeChats: [] as ActiveChat[],

  // Global state for viewing memo details
  memoToView: null as CommunicationItem | null,
  isMemoDialogOpen: false,

  /**
   * Last recipient-state change observed for the current user. Bumped by both
   * the ack form (same-tab, immediately on success) and the inbox realtime
   * channel (cross-tab). Consumers (e.g., Inbox.vue) watch this to patch their
   * local item lists without waiting for a full refetch.
   */
  recipientStateTick: 0,
  lastRecipientStateChange: null as {
    communicationId: string
    action: 'read' | 'acknowledged'
    acknowledgmentLevel?: 'read' | 'accepted' | 'signed'
    at: string
  } | null,

  /**
   * Called after the server confirms an acknowledge/read. Closes the detail
   * dialog, mirrors the new state onto memoToView so the dialog contents
   * don't revert, and bumps the tick so list subscribers patch their rows.
   */
  notifyRecipientStateChanged(
    communicationId: string,
    action: 'read' | 'acknowledged',
    acknowledgmentLevel?: 'read' | 'accepted' | 'signed',
  ) {
    const now = new Date().toISOString()

    if (this.memoToView?.communication_id === communicationId) {
      this.memoToView = {
        ...this.memoToView,
        is_read: true,
        read_at: this.memoToView.read_at ?? now,
        ...(action === 'acknowledged'
          ? {
              is_acknowledged: true,
              is_pending_acknowledgment: false,
              acknowledged_at: now,
              acknowledgment_level: acknowledgmentLevel ?? 'accepted',
            }
          : {}),
      }
    }

    if (action === 'acknowledged') {
      this.isMemoDialogOpen = false
    }

    this.lastRecipientStateChange = {
      communicationId,
      action,
      acknowledgmentLevel,
      at: now,
    }
    this.recipientStateTick += 1
  },

  openChat(conversationId: string, communicationId?: string, title: string = 'Conversation', recipientName?: string) { // Title should be translated at call site
    const existing = this.activeChats.find(chat => chat.conversationId === conversationId)
    if (existing) {
      existing.minimized = false
      return
    }

    // Max 3 chats for now
    if (this.activeChats.length >= 3) {
      this.activeChats.shift()
    }

    this.activeChats.push({
      conversationId,
      communicationId,
      title,
      recipientName,
      minimized: false
    })
  },

  async openDirectChat(targetUserIds: string[], title?: string) {
    try {
      const response = await apiFetch<{ data: { id: string, title: string, subtitle?: string } }>('/api/conversations/direct', {
        method: 'POST',
        body: JSON.stringify({
          user_ids: targetUserIds,
          title: title
        })
      })

      this.openChat(
        response.data.id,
        undefined,
        response.data.title,
        response.data.subtitle
      )
    } catch (error) {
      console.error('Failed to open direct chat:', error)
    }
  },

  async openRecipientChat(recipientId: string) {
    try {
      const response = await apiFetch<{ data: { id: string, title: string, subtitle?: string, communication_id?: string } }>(`/api/recipients/${recipientId}/conversation`, {
        method: 'POST',
      })

      this.openChat(
        response.data.id,
        response.data.communication_id,
        response.data.title,
        response.data.subtitle
      )
    } catch (error) {
      console.error('Failed to open recipient chat:', error)
    }
  },

  closeChat(conversationId: string) {
    const index = this.activeChats.findIndex(chat => chat.conversationId === conversationId)
    if (index !== -1) {
      this.activeChats.splice(index, 1)
    }
  },

  toggleMinimize(conversationId: string) {
    const chat = this.activeChats.find(chat => chat.conversationId === conversationId)
    if (chat) {
      chat.minimized = !chat.minimized
    }
  },

  viewMemo(memo: unknown) {
    const normalized = normalizeCommunicationItem(memo)
    if (!normalized) {
      return
    }

    this.memoToView = normalized
    this.isMemoDialogOpen = true
  },

  async viewCommunication(communicationId: string) {
    if (!communicationId) return;

    try {
      this.isMemoDialogOpen = true;
      // Show loading state if possible, but for now just fetch
      const response = await apiFetch<{ data: unknown }>(`/api/communications/${communicationId}`);
      const normalized = normalizeCommunicationItem(response.data)
      if (!normalized) {
        throw new Error('Invalid communication payload')
      }
      this.memoToView = normalized
    } catch (error) {
      console.error('Failed to fetch communication for viewing:', error);
      this.isMemoDialogOpen = false;
    }
  }
})

export function useMessenger() {
  return messengerState
}
